import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import { useAuth } from '@/hooks/useAuth';
import { useFlags } from '@/hooks/useFlags';
import { addSortFields } from '@/hooks/useSortOptions';
import useSystemPlans from '@/hooks/useSystemPlans';

import { coerceToPlan } from '@/utils/migration';

import Constants from '@/constants';

import {
  AssignableResponse,
  getManagedPlansOptions,
  listAssignableChecklistsOptions,
  Plan,
} from '@/_generated/api-clients/xp';
import { sourceDataFamily } from '@/atoms/data-view';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';

const useGetManagePlansData = () => {
  const { data } = useAuth();
  const { xpCompliancePlans: inHouseCompliancePlansFF } = useFlags();
  const isLicensee = data?.isLicensee;
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);

  const { systemPlans } = useSystemPlans();
  const setSource = useSetAtom(sourceDataFamily('manage-plans'));

  // TODO: content api with compliance courses, remove it when xp api is fully ready.
  const {
    data: unOrderedPlans,
    isFetching: isFetchingAssignablePlans,
    isPending: isPendingAssignablePlansRaw,
    error: assignablePlansDataError,
    refetch: refetchPlansOld,
  } = useQuery({
    ...listAssignableChecklistsOptions(),
    enabled: !inHouseCompliancePlansFF,
    select: data =>
      inHouseCompliancePlansFF
        ? ({
            checklists: [],
            courses: [],
            locationId: undefined,
          } as AssignableResponse)
        : data,
  });

  const isPendingAssignablePlans = inHouseCompliancePlansFF
    ? false
    : isPendingAssignablePlansRaw;

  const {
    data: managedPlansRaw,
    isFetching: isFetchingManagedPlans,
    isPending: isPendingManagedPlans,
    isSuccess: isSuccessManagedPlans,
    error: managedPlansDataError,
    refetch: refetchManagedPlans,
  } = useQuery({
    ...getManagedPlansOptions({ query: { status: 'ACTIVE' } }),
    select: data =>
      !inHouseCompliancePlansFF
        ? data.filter(plan => plan.type !== 'COMPLIANCE')
        : data,
  });

  const isFetchingPlans =
    isFetchingAssignablePlans ||
    isFetchingManagedPlans ||
    isPendingAssignablePlans ||
    isPendingManagedPlans;

  const plansDataError = assignablePlansDataError ?? managedPlansDataError;

  const refetchPlans = () => {
    refetchPlansOld();
    refetchManagedPlans();
  };

  const unOrderedPlansChecklists = unOrderedPlans?.checklists ?? [];
  const unOrderedPlansCourses = unOrderedPlans?.courses ?? [];

  // Use XP Plan[] directly when available; convert legacy checklists as fallback
  const storePlans: Plan[] = isSuccessManagedPlans
    ? managedPlansRaw
    : unOrderedPlansChecklists.map(c => coerceToPlan(c));

  // Legacy compliance courses converted to Plan[]
  const legacyCourses: Plan[] = unOrderedPlansCourses.map(c =>
    coerceToPlan({
      ...c,
      type: 'COMPLIANCE' as const,
      category: Constants.PLAN_CATEGORIES.COMPLIANCE,
    }),
  );

  const plans = useMemo(
    () =>
      isLicensee
        ? storePlans.filter(
            plan => plan.ownerId === activeLicenseeLocation?.number,
          )
        : [...storePlans, ...legacyCourses],
    [isLicensee, storePlans, activeLicenseeLocation, legacyCourses],
  );

  // Set Plan[] (with sort/search fields) as source data
  useEffect(() => {
    if (!isFetchingPlans) {
      setSource(
        plans.map(plan => ({
          ...addSortFields(plan),
          isReadonlySubscribed: systemPlans.some(
            sp => sp.readonly && sp.id === plan.id,
          ),
        })),
      );
    }
  }, [isFetchingPlans, plans, setSource, systemPlans]);

  return {
    plans,
    refetchPlans,
    isFetchingPlans,
    plansDataError,
  };
};

export default useGetManagePlansData;
